import React, { useState } from "react";
import SDClient from "../../../sd_core/client";
import classes from "./checkout-form.module.scss";

const PaymentMethod = (props) => {
  const paymentMethod = props.VOData && props.VOData.order && props.VOData.order.applied_payment_method;
  const [currentMethod, setCurrentMethod] = useState(
    paymentMethod ? paymentMethod.virtual_order_payment_mode : "",
  );
  const [displayErrors, setDisplayErrors] = useState({});

  React.useEffect(() => {
    const availablePaymentMethodIds = Object.values(
      (props.VOData && props.VOData.options && props.VOData.options.payment_methods) || {},
    ).map((method) => method.id);

    if (
      paymentMethod &&
      paymentMethod.virtual_order_payment_mode &&
      availablePaymentMethodIds.includes(paymentMethod.virtual_order_payment_mode)
    ) {
      setCurrentMethod(paymentMethod.virtual_order_payment_mode);
      return;
    }

    if (currentMethod && !availablePaymentMethodIds.includes(currentMethod)) {
      setCurrentMethod("");
      return;
    }

  }, [paymentMethod, props.VOData, currentMethod]);

  const paymentMethodSync = (data) => {
    if (props.onSyncStart) props.onSyncStart();
    SDClient.endpoint("virtual_order_set_payment_method", data).then(
      (d) => {
        if (!d.message.action) {
          let errors = {};
          errors[data.apply_method] = "Unable to set selected payment method.";
          setDisplayErrors(errors);
        }
        props.updateVO();
      },
      () => {
        let errors = {};
        errors[data.apply_method] = "Unable to set selected payment method.";
        setDisplayErrors(errors);
        props.updateVO();
      },
    );
  };

  const icons = {
    online_payment_razorpay: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        width="28"
        height="28"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z"
        />
      </svg>
    ),
    payment_on_delivery: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        width="28"
        height="28"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z"
        />
      </svg>
    ),
    gift_card_full: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        width="28"
        height="28"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z"
        />
      </svg>
    ),
  };

  const labels = {
    online_payment_razorpay: {
      title: "Pay Online",
      desc: "Secure payments via Razorpay",
    },
    payment_on_delivery: {
      title: "Cash on Delivery",
      desc: "Pay at your doorstep",
    },
    gift_card_full: {
      title: "Paid with Gift Card",
      desc: "Fully covered by your gift card balance",
    },
  };

  const loadingPreview = () => {
    return (
      <div className={classes.paymentOptions}>
        <div className={classes.skeletonInput} />
        <div className={classes.skeletonInput} />
      </div>
    );
  };

  const showMethods = () => {
    const availablePaymentMethods = props.VOData.options.payment_methods;
    const appliedGiftCard =
      props.VOData.order && props.VOData.order.applied_gift_card;
    // A gift card counts toward payment only once its OTP transfer is verified.
    // Until then, the gift_card_full option must not be selectable.
    const giftCardPending = !!(
      appliedGiftCard &&
      appliedGiftCard.virtual_order_redeem_status !== "transfer_verified"
    );

    return (
      <div className={classes.paymentOptions}>
        {Object.entries(availablePaymentMethods).map(([k, v]) => {
          const isSelected = currentMethod === v.id;
          const label = labels[v.id] || { title: v.method, desc: "" };
          const isLocked = giftCardPending && v.id === "gift_card_full";
          return (
            <div key={v.id}>
              <div
                className={
                  isSelected ? classes.paymentCardSelected : classes.paymentCard
                }
                style={
                  isLocked ? { opacity: 0.5, pointerEvents: "none" } : undefined
                }
                onClick={() => {
                  if (isLocked) return;
                  setCurrentMethod(v.id);
                  paymentMethodSync({ apply_method: v.id });
                }}
              >
                <div className={classes.paymentCardLeft}>
                  <div className={classes.paymentIcon}>
                    {icons[v.id] || null}
                  </div>
                  <div>
                    <p className={classes.paymentLabel}>{label.title}</p>
                    <p className={classes.paymentDesc}>{label.desc}</p>
                  </div>
                </div>
                <input
                  className={classes.paymentRadio}
                  type="radio"
                  name="virtual_order_payment_mode"
                  checked={isSelected}
                  readOnly
                />
              </div>
              {isLocked && (
                <p className={classes.paymentError}>
                  Verify your gift card OTP above to use this payment method.
                </p>
              )}
              {displayErrors[v.id] && (
                <p className={classes.paymentError}>{displayErrors[v.id]}</p>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className={classes.paymentSection}>
      <div>
        <h3 className={classes.title}>Payment Method</h3>
        <p className={classes.subtitle}>
          Select a payment method for checkout.
        </p>
      </div>
      {props.syncing && (!props.VOData || !props.VOData.options)
        ? loadingPreview()
        : <div className={props.syncing ? classes.syncingDisabled : undefined}>{showMethods()}</div>}
    </div>
  );
};

export default PaymentMethod;
